import React from 'react'
import { format } from 'date-fns'
import {
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { Box, Link } from '@mui/material'
import { AbsenceDay } from '../../../types/absence-day'
import ActionIcon from '../../../shared/UI/ActionIcon'
import { getPayApprovalHoursDisplayValue, getUnpaidHoursDisplayValue, isBereavementOrFuneral } from '../../../utils/absence-utils'
import { AbsenceStatusType } from '../../../utils/constants'
import { AbsenceReasonTypesEnum } from '../../Absence/types'
import palette from '../../../theme/palette'

interface ColumnDefinitionParams {
  isSickness: boolean
  onEdit?: (row: AbsenceDay) => void
  onDelete: (row: AbsenceDay) => void
  userPermissions: string[]
  viewOnly?: boolean
  handleViewClick?: (row: AbsenceDay) => void
  isPayrollApproverReadOnlyView?: boolean
  isRtwComplete?: boolean
  rtwRequired?: boolean
  absenceStatusType?: string | null
  absenceTypeId?: number
  isManager?: boolean
}

const sicknessOnlyColumns = ['sickNoteAdded']
const bereavementOrFuneralExcludedColumns = ['hoursLost']

const formatBooleanCell = (value: boolean | null | undefined): string => {
  if (value == null) return '–'
  return value ? 'Yes' : 'No'
}

export function createAbsenceDetailColumns(options: ColumnDefinitionParams): GridColDef[] {
  const {
    isSickness,
    onEdit,
    onDelete,
    userPermissions,
    viewOnly = false,
    handleViewClick,
    isPayrollApproverReadOnlyView = false,
    isRtwComplete,
    rtwRequired = true,
    absenceStatusType,
    absenceTypeId,
    isManager = false,
  } = options

  const isAwaitingSca = absenceStatusType === AbsenceStatusType.AWAITING_SCA
  const isEditDisabled = isPayrollApproverReadOnlyView || isRtwComplete || (!rtwRequired && !isAwaitingSca)
  const isDeleteDisabled = isPayrollApproverReadOnlyView || isRtwComplete || (!rtwRequired && !isAwaitingSca)

  const isBereavementOrFuneralAbsence = isBereavementOrFuneral(absenceTypeId)

  const unpaidHeaderName =
    {
      [AbsenceReasonTypesEnum.Bereavement]: 'Bereavement Hours',
      [AbsenceReasonTypesEnum.FuneralAttendance]: 'Funeral Hours',
    }[absenceTypeId as number] || 'Unpaid'

  const actionColumns: GridColDef[] = []

  if (viewOnly && handleViewClick) {
    actionColumns.push({
      field: 'actions',
      headerName: '',
      width: 60,
      minWidth: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (cellParams: GridRenderCellParams<AbsenceDay>) => (
        <ActionIcon onClick={() => handleViewClick(cellParams.row)}>
          <Link href="#" variant="body2">
            View
          </Link>
        </ActionIcon>
      ),
    })
  } else if (isManager) {
    actionColumns.push({
      field: 'actions',
      headerName: '',
      width: 96,
      minWidth: 96,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (cellParams: GridRenderCellParams<AbsenceDay>) => (
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <ActionIcon disabled={isEditDisabled}>
            <CreateOutlinedIcon
              color={isPayrollApproverReadOnlyView ? undefined : 'primary'}
              onClick={() => onEdit?.(cellParams.row)}
            />
          </ActionIcon>
          {userPermissions.includes('DeleteAttendance') && (
            <ActionIcon disabled={isDeleteDisabled}>
              <DeleteOutlinedIcon
                color={isPayrollApproverReadOnlyView ? undefined : 'primary'}
                onClick={() => onDelete(cellParams.row)}
              />
            </ActionIcon>
          )}
        </Box>
      ),
    })
  }

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      width: 105,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <span>{value ? format(new Date(value), 'd MMM yyyy') : 'N/A'}</span>
      ),
    },
    {
      field: 'hoursLost',
      headerName: 'Absence Hours',
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
          renderCell: ({ value }) => {
              const numValue = value ?? 0
              if (numValue === 0) return <span style={{ color: palette.type.multipleRequests.color }}>0.00</span>
              return <span style={{ color: palette.type.absence.color }}>{`-${Math.abs(numValue).toFixed(2)}`}</span>
          },
      },
    {
      field: 'toWorkBack',
      headerName: 'To Work Back',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => <span>{formatBooleanCell(value)}</span>,
    },
    {
      field: 'sickNoteAdded',
      headerName: 'Sick Note Added',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => <span>{formatBooleanCell(value)}</span>,
    },
    ...(!isBereavementOrFuneralAbsence && isManager
      ? [
          {
            field: 'payApproval',
            headerName: 'Discretionary Pay',
            flex: 1,
            minWidth: 140,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            headerAlign: 'center' as const,
            align: 'center' as const,
            renderCell: (params: GridRenderCellParams) => {
              const { manAuthRequestStatus } = params.row
              return (
                <span>{getPayApprovalHoursDisplayValue(params.value, manAuthRequestStatus)}</span>
              )
            },
          },
        ]
      : []),
    {
      field: 'unpaid',
      headerName: unpaidHeaderName,
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <span>
          {getUnpaidHoursDisplayValue(params.value, params.row.payApproval, params.row.manAuthRequestStatus)}
        </span>
      ),
    },
    ...actionColumns,
  ]

  return columns.filter(col => {
    if (!isSickness && sicknessOnlyColumns.includes(col.field)) return false
    if (isBereavementOrFuneralAbsence && bereavementOrFuneralExcludedColumns.includes(col.field)) return false
    return true
  })
}
